import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MapIcon } from '@schamburg-web/interfaces';

interface ModuleState {
  allObjects: MapIcon[];
  displayObjects: MapIcon[];
  lastUpdated?: string;
  isLoading: boolean;
}

const createInitialState = (): ModuleState => ({
  allObjects: [],
  displayObjects: [],
  lastUpdated: undefined,
  isLoading: false,
});

export const createModuleSlice = (moduleName: string) => {
  const slice = createSlice({
    name: moduleName,
    initialState: createInitialState(),
    reducers: {
      setLoading: (state, action: PayloadAction<boolean>) => {
        state.isLoading = action.payload;
      },
      setAllObjects: (state, action: PayloadAction<MapIcon[]>) => {
        state.allObjects = action.payload;
        state.lastUpdated = new Date().toISOString();
        state.isLoading = false;
      },
      setDisplayObjects: (state, action: PayloadAction<MapIcon[]>) => {
        state.displayObjects = action.payload;
      },
      updateObject: (
        state,
        action: PayloadAction<{ id: string; updates: Partial<MapIcon> }>
      ) => {
        const { id, updates } = action.payload;

        // Update in allObjects
        const allObjectIndex = state.allObjects.findIndex(
          (obj) => obj.id === id
        );
        if (allObjectIndex !== -1) {
          state.allObjects[allObjectIndex] = {
            ...state.allObjects[allObjectIndex],
            ...updates,
          };
        }

        // Update in displayObjects
        const displayObjectIndex = state.displayObjects.findIndex(
          (obj) => obj.id === id
        );
        if (displayObjectIndex !== -1) {
          state.displayObjects[displayObjectIndex] = {
            ...state.displayObjects[displayObjectIndex],
            ...updates,
          };
        }

        state.lastUpdated = new Date().toISOString();
      },
      clearObjects: (state) => {
        state.allObjects = [];
        state.displayObjects = [];
        state.lastUpdated = undefined;
        state.isLoading = false;
      },
      resetModule: (state) => {
        Object.assign(state, createInitialState());
      },
    },
  });

  // Return both the slice and typed action creators
  return {
    slice,
    actions: slice.actions,
    reducer: slice.reducer,
    // Typed selectors
    selectors: {
      selectAllObjects: (state: { [key: string]: ModuleState }) =>
        state[moduleName]?.allObjects || [],
      selectDisplayObjects: (state: { [key: string]: ModuleState }) =>
        state[moduleName]?.displayObjects || [],
      selectLastUpdated: (state: { [key: string]: ModuleState }) =>
        state[moduleName]?.lastUpdated,
      selectIsLoading: (state: { [key: string]: ModuleState }) =>
        state[moduleName]?.isLoading ?? false,
      selectModuleState: (state: { [key: string]: ModuleState }) =>
        state[moduleName] || createInitialState(),
    },
  };
};

// Type exports for consumers
export type { ModuleState };
export type ModuleSlice = ReturnType<typeof createModuleSlice>;
